import { useCallback, useEffect, useState, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FormSection } from '@/components/shared/FormSection';
import { EditPageLayout } from '@/components/shared/EditPageLayout';
import { useIndustryTranslation } from '@/hooks/useIndustryTranslation';
import { useTenantSwitcher } from '@/contexts/TenantSwitcherContext';
import {
  getAvailableBranchTypes,
  getBranchTypeTranslationKey,
} from '@/utils/branchTypes';
import type { Branch, BranchType } from '@packages/shared-types';
import {
  getBranchById,
  type UpdateBranchPayload,
  updateBranch,
  deleteBranch,
} from '@/services/branchService';

const branchFormSchema = z.object({
  name: z.string().min(1, 'pages.branches.form.nameRequired'),
  type: z.enum(['retail_store', 'warehouse', 'distribution_center'], {
    message: 'pages.branches.form.typeRequired',
  }),
  address: z.string().optional(),
});

type BranchFormValues = z.infer<typeof branchFormSchema>;

const BRANCH_TYPES: Array<{ value: BranchType; labelKey: string }> = [
  { value: 'retail_store', labelKey: 'pages.branches.form.typeOptions.retail_store' },
  { value: 'warehouse', labelKey: 'pages.branches.form.typeOptions.warehouse' },
  {
    value: 'distribution_center',
    labelKey: 'pages.branches.form.typeOptions.distribution_center',
  },
];

export function EditBranchPage(): JSX.Element {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { activeTenant } = useTenantSwitcher();
  const editTitle = useIndustryTranslation('pages.branches.edit.title');
  const editDescription = useIndustryTranslation('pages.branches.edit.description');
  const deleteDialogTitle = useIndustryTranslation('pages.branches.dialogs.delete.title');
  const successUpdate = useIndustryTranslation('pages.branches.success.update');
  const successDelete = useIndustryTranslation('pages.branches.success.delete');
  const errorLoad = useIndustryTranslation('pages.branches.error.load');
  const errorUpdate = useIndustryTranslation('pages.branches.error.update');
  const errorDelete = useIndustryTranslation('pages.branches.error.delete');
  const errorNotFound = useIndustryTranslation('pages.branches.error.notFound');

  const [branch, setBranch] = useState<Branch | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<BranchFormValues>({
    resolver: zodResolver(branchFormSchema),
    defaultValues: {
      name: '',
      type: 'retail_store',
      address: '',
    },
  });

  const formatErrorMessage = useCallback(
    (message?: string): string | null => {
      if (!message) {
        return null;
      }

      return t(message);
    },
    [t],
  );

  const availableBranchTypes = useMemo(() => {
    const allowedTypes = getAvailableBranchTypes(activeTenant?.industry);
    return BRANCH_TYPES.filter((bt) => allowedTypes.includes(bt.value));
  }, [activeTenant?.industry]);

  const loadBranch = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await getBranchById(id);
      setBranch(data);
      reset({
        name: data.name,
        type: data.type,
        address: data.address || '',
      });
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : errorLoad,
      );
    } finally {
      setIsLoading(false);
    }
  }, [id, reset, errorLoad, activeTenant?.id]);

  useEffect(() => {
    void loadBranch();
  }, [loadBranch]);

  const handleSubmitBranch = async (
    values: BranchFormValues,
  ): Promise<void> => {
    if (!id) return;
    setIsSaving(true);
    setError(null);

    const payload: UpdateBranchPayload = {
      name: values.name,
      type: values.type,
      address: values.address || undefined,
    };

    try {
      await updateBranch(id, payload);
      await loadBranch();
      toast.success(successUpdate);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : errorUpdate;
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!id || !branch) return;
    setIsDeleting(true);
    try {
      await deleteBranch(id);
      toast.success(successDelete);
      navigate('/branches');
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : errorDelete;
      setError(errorMessage);
      toast.error(errorMessage);
      setIsDeleting(false);
    }
  };

  const handleFormSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    void handleSubmit(handleSubmitBranch)();
  };

  if (!id) {
    return (
      <div className="p-6 text-sm text-destructive">
        {errorNotFound}
      </div>
    );
  }

  return (
    <EditPageLayout
      title={editTitle}
      description={branch ? editDescription : undefined}
      isLoading={isLoading}
      error={error}
      formId="branch-edit-form"
      onSubmit={handleFormSubmit}
      onCancel={() => navigate(-1)}
      cancelLabel={t('pages.branches.actions.cancel')}
      onSave={() => void handleSubmit(handleSubmitBranch)()}
      saveLabel={t('pages.branches.actions.update')}
      savingLabel={t('pages.branches.actions.updating')}
      isSaving={isSaving}
      onDelete={handleDelete}
      deleteLabel={t('pages.branches.actions.delete')}
      deletingLabel={t('pages.branches.actions.deleting')}
      isDeleting={isDeleting}
      deleteDialogTitle={deleteDialogTitle}
      deleteDialogDescription={t('pages.branches.dialogs.delete.description', {
        name: branch?.name,
      })}
      entityExists={!!branch}
    >
      <form
        id="branch-edit-form"
        className="space-y-6"
        onSubmit={handleSubmit(handleSubmitBranch)}
      >
        <FormSection
          title={t('pages.branches.form.name')}
          description={t('pages.branches.form.name')}
          error={
            errors.name
              ? formatErrorMessage(errors.name.message) ??
                (errors.name.message as string)
              : null
          }
        >
          <Input
            id="branch-name"
            {...register('name')}
            placeholder={t('pages.branches.form.name')}
            disabled={isSaving}
          />
        </FormSection>
        <FormSection
          title={t('pages.branches.form.type')}
          description={t('pages.branches.form.selectType')}
          error={
            errors.type
              ? formatErrorMessage(errors.type.message) ??
                (errors.type.message as string)
              : null
          }
        >
          <Controller
            name="type"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value}
                onValueChange={field.onChange}
                disabled={isSaving}
              >
                <SelectTrigger id="branch-type">
                  <SelectValue
                    placeholder={t('pages.branches.form.selectType')}
                  />
                </SelectTrigger>
                <SelectContent>
                  {availableBranchTypes.map((branchType) => {
                    const industry = activeTenant?.industry;
                    const translationKey = getBranchTypeTranslationKey(
                      branchType.value,
                      industry,
                    );
                    const translated = t(translationKey);
                    const label =
                      translated !== translationKey
                        ? translated
                        : t(branchType.labelKey);
                    return (
                      <SelectItem
                        key={branchType.value}
                        value={branchType.value}
                      >
                        {label}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            )}
          />
        </FormSection>
        <FormSection
          title={t('pages.branches.form.address')}
          description={t('pages.branches.form.address')}
        >
          <Input
            id="branch-address"
            {...register('address')}
            placeholder={t('pages.branches.form.address')}
            disabled={isSaving}
          />
        </FormSection>
      </form>
    </EditPageLayout>
  );
}
